function parsePortalDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  // Parse database date-only values locally so UTC conversion cannot shift the day.
  const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]), 12);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function ordinalSuffix(day) {
  const remainder100 = day % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return 'th';
  if (day % 10 === 1) return 'st';
  if (day % 10 === 2) return 'nd';
  if (day % 10 === 3) return 'rd';
  return 'th';
}

function getDateParts(date, timeZone) {
  if (!timeZone) {
    return {
      month: date.toLocaleDateString('en-CA', { month: 'long' }),
      day: date.getDate(),
      year: date.getFullYear(),
    };
  }

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  }).formatToParts(date);

  return {
    month: parts.find((part) => part.type === 'month')?.value || '',
    day: Number(parts.find((part) => part.type === 'day')?.value || date.getDate()),
    year: Number(parts.find((part) => part.type === 'year')?.value || date.getFullYear()),
  };
}

function parseWallClockDateParts(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (!match) return null;

  return {
    year: Number(match[1]),
    monthIndex: Number(match[2]) - 1,
    day: Number(match[3]),
    hour: Number(match[4] || 0),
    minute: Number(match[5] || 0),
    second: Number(match[6] || 0),
  };
}

function formatWallClockDate(value, options = {}) {
  const parts = parseWallClockDateParts(value);
  if (!parts) return null;

  const date = new Date(parts.year, parts.monthIndex, parts.day, parts.hour, parts.minute, parts.second);
  if (Number.isNaN(date.getTime())) return null;

  const month = date.toLocaleDateString('en-CA', { month: 'long' });
  const dateText = `${month} ${String(parts.day).padStart(2, '0')}${ordinalSuffix(parts.day)}, ${parts.year}`;
  if (!options.includeTime) return dateText;

  const timeText = date.toLocaleTimeString('en-CA', {
    hour: '2-digit',
    minute: '2-digit',
    ...(options.includeSeconds ? { second: '2-digit' } : {}),
  });
  return `${dateText} at ${timeText}`;
}

export function formatPortalDate(value, options = {}) {
  if (options.preserveWallTime) {
    const wallClockText = formatWallClockDate(value, options);
    if (wallClockText) return wallClockText;
  }

  const date = parsePortalDate(value);
  if (!date) return '—';

  const { month, day, year } = getDateParts(date, options.timeZone);
  const dateText = `${month} ${String(day).padStart(2, '0')}${ordinalSuffix(day)}, ${year}`;
  if (!options.includeTime) return dateText;

  const timeText = date.toLocaleTimeString('en-CA', {
    hour: '2-digit',
    minute: '2-digit',
    ...(options.timeZone ? { timeZone: options.timeZone } : {}),
    ...(options.includeSeconds ? { second: '2-digit' } : {}),
  });
  return `${dateText} at ${timeText}`;
}
